import { useEffect, useRef, useState } from 'react';
import { t } from '../i18n/idioma';
import type { DueloEstado } from '../world/types';
import { relogio } from './dueloTempo';

interface Props {
  estado: DueloEstado;
  faltaRodada: number;
  faltaAusente: number;
  /** Fim de rodada na tela (a faixa de quem venceu): o relógio mostra quanto falta para a próxima. */
  faltaLuta: number;
  entreRodadas: boolean;
  pipNovo: '' | 'voce' | 'ele';
  onDesistir(): void;
}

function modoRotulo(modo: string): string {
  return modo === 'com_loot' ? t('duelo.comLoot') : t('duelo.semLoot');
}

function Pedras({ total, acesas, novo }: { total: number; acesas: number; novo: boolean }) {
  const n = Math.max(1, total);
  return (
    <span className="pips" aria-hidden="true">
      {Array.from({ length: n }, (_, i) => (
        <span key={i} className={`pip${i < acesas ? ' pip--ganho' : ''}${novo && i === acesas - 1 ? ' pip--novo' : ''}`} />
      ))}
    </span>
  );
}

/**
 * A faixa do duelo (duelo v2, Renato, 29/10/2026): o placar em pedras, a rodada e o relógio, pequena no topo. A vida
 * dos dois saiu daqui: a sua placa e a placa do alvo (presa no adversário) já mostram. Desistir mora no menu ⋯, junto
 * do modo, da aposta e da regra do tempo.
 */
export function DueloPlacar({ estado, faltaRodada, faltaAusente, faltaLuta, entreRodadas, pipNovo, onDesistir }: Props) {
  const [menu, setMenu] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const fora = (e: PointerEvent) => { if (!caixa.current?.contains(e.target as Node)) setMenu(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(false); };
    window.addEventListener('pointerdown', fora);
    window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('pointerdown', fora); window.removeEventListener('keydown', esc); };
  }, [menu]);

  const decisiva = estado.placarVoce === estado.rodadasPara - 1
    && estado.placarEle === estado.rodadasPara - 1
    && estado.rodadasPara > 1;
  const ausente = estado.estado === 'ausente';
  const luta = estado.estado === 'luta';
  const fimRelogio = luta && faltaRodada <= 30_000;
  const melhor = estado.rodadasPara * 2 - 1;
  const rotulo = ausente
    ? t('duelo.caiuNome', { nome: estado.adversario })
    : entreRodadas
      ? t('duelo.proximaEm')
      : decisiva ? t('duelo.decisiva') : t('duelo.rodadaMelhor', { n: estado.rodada, m: melhor });
  const tempo = luta
    ? relogio(faltaRodada)
    : ausente ? relogio(faltaAusente)
      : entreRodadas ? relogio(Math.max(0, faltaLuta)) : relogio(estado.rodadaDuracaoMs || 0);
  const trilho = luta && estado.rodadaDuracaoMs > 0
    ? Math.max(0, Math.min(100, faltaRodada / estado.rodadaDuracaoMs * 100))
    : 100;

  return (
    <div className="dz-faixa" ref={caixa} role="group" aria-label={t('duelo.placar', { voce: estado.placarVoce, ele: estado.placarEle, nome: estado.adversario })}>
      <div className="dz-lado dz-lado--voce" aria-label={t('duelo.lado', { nome: t('duelo.voce'), v: estado.placarVoce, total: estado.rodadasPara })}>
        <Pedras total={estado.rodadasPara} acesas={estado.placarVoce} novo={pipNovo === 'voce'} />
        <span className="dz-lado__nome">{t('duelo.voce')}</span>
      </div>
      <div className="dz-meio">
        <span className={`dz-meio__rodada${decisiva && !ausente && !entreRodadas ? ' is-decisiva' : ''}`}>{rotulo}</span>
        <span className={`dz-meio__tempo${fimRelogio ? ' is-fim' : luta ? '' : ' is-pausa'}`} role={luta ? 'timer' : undefined}>{tempo}</span>
      </div>
      <div className={`dz-lado dz-lado--ele${ausente ? ' is-caiu' : ''}`} aria-label={t('duelo.lado', { nome: estado.adversario, v: estado.placarEle, total: estado.rodadasPara })}>
        <span className="dz-lado__nome">{estado.adversario}</span>
        <Pedras total={estado.rodadasPara} acesas={estado.placarEle} novo={pipNovo === 'ele'} />
      </div>
      <span className={`dz-trilho${fimRelogio ? ' is-fim' : ''}`} aria-hidden="true"><i style={{ width: `${trilho}%` }} /></span>
      <button
        type="button"
        className="dz-mais"
        aria-label={t('duelo.opcoes')}
        aria-haspopup="menu"
        aria-expanded={menu}
        onClick={() => setMenu(v => !v)}
      >
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><circle cx="3" cy="8" r="1.6" /><circle cx="8" cy="8" r="1.6" /><circle cx="13" cy="8" r="1.6" /></svg>
      </button>
      {menu && (
        <div className="dz-menu" role="menu">
          <div className="dz-tags">
            <span className={`dz-chip${estado.modo === 'com_loot' ? ' dz-chip--loot' : ''}`}>{modoRotulo(estado.modo)}</span>
            <span className={`dz-chip${estado.comAposta ? ' dz-chip--aposta' : ''}`}>{estado.comAposta ? t('duelo.comAposta') : t('duelo.semAposta')}</span>
          </div>
          <small>{t('duelo.regraTempo', { n: melhor, tempo: relogio(estado.rodadaDuracaoMs || 0) })}</small>
          <button type="button" className="dz-desistir" role="menuitem" onClick={() => { setMenu(false); onDesistir(); }}>
            ⚑ {t('duelo.desistirMenu')}
          </button>
        </div>
      )}
      {ausente && (
        <p className="dz-aviso">{t('duelo.ausenteAviso', { nome: estado.adversario, tempo: relogio(faltaAusente) })}</p>
      )}
    </div>
  );
}
